import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/shared/providers/AuthContext';
import { apiClient } from '@shared/lib/axios';
import { clinicApiPath } from '@shared/lib/apiPath';
import { fetchInsurerDetail } from '@pages/main/patient/services';

/**
 * Fixed insurer number that represents 生活保護 (Welfare). The actual `insurer_name`
 * and `insurer_kind_name` live on the matching row in the `insurers` table — they
 * are NOT hardcoded constants on the client.
 */
export const SEIKATSU_HOGO_INSURER_NUMBER = '00000001';

export type DefaultSeikatsuHogoInsurer = {
  id: number;
  insurer_number: string;
  insurer_name: string;
  insurer_kind_name: string;
  patient_cost_rate?: string | number | null;
  cost_rate: number | null;
  family_cost_rate: number | null;
  medix_claim_type_id?: number | null;
};

type SearchResponse = {
  success?: boolean;
  data?:
    | Array<Record<string, unknown>>
    | { data?: Array<Record<string, unknown>> };
};

const toFiniteId = (v: unknown): number | null => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

/**
 * Pick the canonical insurer when the search returns more than one row for the
 * same `insurer_number` — the master `insurers` table can contain duplicates
 * after manual data edits. Lowest `id` wins (i.e. the original seed/canonical
 * record), matching the existing patient data which references `insurer_id: 1`.
 */
const pickCanonicalInsurer = (
  resp: SearchResponse
): { id?: unknown } | undefined => {
  const raw = resp.data;
  const list = Array.isArray(raw) ? raw : raw?.data;
  if (!Array.isArray(list) || list.length === 0) return undefined;

  let best: Record<string, unknown> | undefined;
  let bestId = Number.POSITIVE_INFINITY;
  for (const row of list) {
    const id = toFiniteId((row as { id?: unknown }).id);
    if (id !== null && id < bestId) {
      bestId = id;
      best = row;
    }
  }
  return best ?? (list[0] as { id?: unknown });
};

/**
 * Fetch the canonical 生活保護 insurer record (the one with `insurer_number = "00000001"`)
 * once per clinic and cache it. Used by the type-3 autofill paths so they populate
 * `insurer_id` / `insurer_name` / `insurer_kind_name` from real DB values instead of
 * hardcoded strings.
 */
export const useDefaultSeikatsuHogoInsurer = () => {
  const { clinic } = useAuth();
  const siteType = clinic?.site_type;
  const clinicNumber = clinic?.clinic_number;

  return useQuery<DefaultSeikatsuHogoInsurer | null>({
    queryKey: ['default-seikatsu-hogo-insurer', siteType, clinicNumber],
    enabled: !!siteType && !!clinicNumber,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const searchResp = await apiClient.get<SearchResponse>(
        clinicApiPath(siteType!, clinicNumber!, '/insurers/search'),
        { params: { insurer_number: SEIKATSU_HOGO_INSURER_NUMBER } }
      );
      const picked = pickCanonicalInsurer(searchResp.data);
      const id = toFiniteId(picked?.id);
      if (id === null) return null;

      const detail = await fetchInsurerDetail(siteType!, clinicNumber!, id);
      return {
        id: detail.id,
        insurer_number: detail.insurer_number ?? SEIKATSU_HOGO_INSURER_NUMBER,
        insurer_name: detail.insurer_name ?? '',
        insurer_kind_name:
          (detail as { insurer_kind_name?: string | null }).insurer_kind_name ||
          detail.insurer_type?.name ||
          '',
        patient_cost_rate: (detail as { patient_cost_rate?: string | number | null })
          .patient_cost_rate,
        cost_rate: detail.cost_rate ?? null,
        family_cost_rate: detail.family_cost_rate ?? null,
        medix_claim_type_id: detail.medix_claim_type_id ?? null,
      };
    },
  });
};
